import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Logo } from "../../../../../../../models/company";
import { Signature } from "../../../../../../../models/signature";
import SignatureService from "../../../../../../../services/signature.service";
import { SignatureKeys } from "../../../../../../../util/queries";

export const useActions = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [signature, setSignature] = useState<Logo>();
  const [signatureToUpdate, setSignatureToUpdate] = useState<Signature>();
  const [hasFormBeenChanged, setHasFormBeenChanged] = useState<Boolean>();
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<Boolean>();

  const signatureObject: Signature = {
    picture: signature,
    companyId: currentUser?.companyId,
  };

  const isValid = () => {
    let valid = true;

    if (!signatureObject.picture) valid = false;

    return valid;
  };

  const { refetch, isFetching } = useQuery({
    queryKey: SignatureKeys.getById(currentUser?.companyId),
    queryFn: () => SignatureService.getById(Number(currentUser?.companyId)),
    enabled: !!currentUser?.id && !!currentUser?.companyId,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response.data) {
        setSignatureToUpdate(response.data);
        setSignature(response.data.picture);
      }
    },
  });

  const { mutate: updateSignature, isLoading: isUpdating } = useMutation({
    mutationKey: SignatureKeys.update(),
    mutationFn: ({ id, signature }: { id: number; signature: Signature }) =>
      SignatureService.update(id, signature),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.signature",
      });
      refetch();
    },
  });

  const { mutate: createSignature, isLoading: isCreating } = useMutation({
    mutationKey: SignatureKeys.create(),
    mutationFn: (signature: Signature) => SignatureService.create(signature),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      refetch();
    },
  });

  const isLoading = isCreating || isUpdating;

  const onSaveSignature = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (signatureToUpdate && signatureToUpdate.id) {
      if (signatureToUpdate) {
        const object = {
          ...signatureToUpdate,
          ...signatureObject,
        };

        updateSignature({ id: signatureToUpdate.id, signature: object as Signature });
      }
    } else {
      const object = {
        ...signatureObject,
      } as Signature;

      if (isValid()) createSignature(object);
    }
  };
  const onCancelSignature = () => {
    setSignature(signatureToUpdate?.picture);
  };

  return {
    signature,
    signatureToUpdate,
    isFetching,
    isCreating,
    isUpdating,
    hasFormBeenChanged,
    hasFormBeenSubmitted,
    signatureObject,
    isLoading,
    onCancelSignature,
    onSaveSignature,
    setSignature,
    setHasFormBeenSubmitted,
    setHasFormBeenChanged,
    setSignatureToUpdate,
    updateSignature,
    createSignature,
    isValid,
  };
};
